import { createSelector, createSlice } from '@reduxjs/toolkit';
import { getCronSchedule } from '@homeplay/utils';

const initialState = {
  jackpot: {
    isJackpotsEnabled: false,
    lifeCycles: {},
    wonNotifications: [],
    recentlyWonJackpots: [],
    isInitialized: false,
  },
  happyHour: null,
  happyHourBonus: {
    isComplete: false,
    isCongratsBanner: false,
    fetched: false,
    bonusStates: null,
  },
  isHhOverlayOpen: false,
  happyHourConfigs: null,
};

export const promotionsSlice = createSlice({
  name: 'promotions',
  initialState,
  reducers: {
    setHappyHour: (state, action) => {
      if (!action.payload.happyHour) {
        state.happyHour = null;
      } else if (
        !state.happyHour ||
        state.happyHour.id === action.payload.happyHour.id
      ) {
        state.happyHour = action.payload.happyHour;
      }
    },
    setHappyHourBonus: (state, action) => {
      state.happyHourBonus = {
        isComplete: action.payload.isComplete,
        isCongratsBanner: action.payload.isCongratsBanner
          ? action.payload.isCongratsBanner
          : null,
        bonusStates: action.payload.bonusStates,
        fetched: true,
      };
    },
    updateHappyHourBonusState: (state, action) => {
      if (action.payload.bonusState.complete) {
        state.happyHourBonus.isComplete = true;
        state.happyHourBonus.isCongratsBanner = true;

        if (
          state.happyHour &&
          state.happyHour.id === action.payload.bonusState.promotionId
        ) {
          state.happyHour = null;
        }
      }
      const bonusStates = state.happyHourBonus.bonusStates
        ? [...state.happyHourBonus.bonusStates]
        : [];
      const stateIndex = bonusStates.findIndex(
        (state) => state.id === action.payload.bonusState.id
      );

      if (stateIndex >= 0) {
        bonusStates[stateIndex] = action.payload.bonusState;
      } else {
        bonusStates.push(action.payload.bonusState);
      }
      state.happyHourBonus.bonusStates = bonusStates;
      state.happyHourBonus.fetched = true;
    },
    removeHappyHourBonusState: (state, action) => {
      if (state.happyHourBonus.bonusStates) {
        let isComplete = false;
        state.happyHourBonus.bonusStates =
          state.happyHourBonus.bonusStates.filter((state) => {
            if (state.id !== action.payload.bonusStateId) {
              if (state.complete) {
                isComplete = true;
              }
              return true;
            }
            return false;
          });
        state.happyHourBonus.isComplete = isComplete;
        if (!isComplete) {
          state.happyHourBonus.isCongratsBanner = false;
        }
      }
    },
    setIsCongratsBanner: (state, action) => {
      state.happyHourBonus.isCongratsBanner = action.payload.value;
    },
    resetHappyHour: (state, action) => {
      state.happyHour = null;
      state.happyHourBonus = {
        isComplete: false,
        isCongratsBanner: false,
        fetched: false,
        bonusStates: null,
      };
    },
    setHappyHourConfigs: (state, action) => {
      state.happyHourConfigs = action.payload;
    },
    setJackpotsLifecycles: (state, action) => {
      const lifecycles = action.payload.jackpot_lifecycles || {};
      state.jackpot.isJackpotsEnabled = action.payload.is_jackpots_enabled && Object.keys(lifecycles).length > 0;
      state.jackpot.lifeCycles = lifecycles;
      state.jackpot.isInitialized = true;
    },
    updateJackpotsLifecycles: (state, action) => {
      const updateLifecycle = (key, newData) => {
        if (key && state.jackpot.lifeCycles.hasOwnProperty(key)) {
          state.jackpot.lifeCycles[key] = {
            ...state.jackpot.lifeCycles[key],
            ...newData,
          };
        }
      };

      switch (action.payload.action) {
        case 'init': {
          const newJackpotData = Object.values(
            action.payload.jackpot_lifecycle
          )[0];

          const filteredJackpots = Object.fromEntries(
            Object.entries(state.jackpot.lifeCycles).filter(([_key, value]) => {
              return value?.slug !== newJackpotData.slug;
            })
          );

          state.jackpot.lifeCycles = {
            ...filteredJackpots,
            ...action.payload.jackpot_lifecycle,
          };
          break;
        }

        case 'update': {
          const [keyForUpdate, newJackpotData] = Object.entries(
            action.payload.jackpot_lifecycle
          )[0];
          if (
            keyForUpdate &&
            state.jackpot.lifeCycles.hasOwnProperty(keyForUpdate)
          ) {
            state.jackpot.lifeCycles[keyForUpdate] = {
              ...state.jackpot.lifeCycles[keyForUpdate],
              winnerName: undefined,
            };
          }

          updateLifecycle(keyForUpdate, newJackpotData);
          break;
        }

        case 'won': {
          const {
            win_lifecycle: { id, winner_name },
            is_won,
          } = action.payload;

          if (is_won) {
            state.jackpot.wonNotifications.push(action.payload.win_lifecycle);
          }

          updateLifecycle(id.toString(), { winnerName: winner_name });
          break;
        }

        case 'error':
        case 'stopped': {
          const keyForUpdate = Object.keys(action.payload.jackpot_lifecycle)[0];
          if (
            keyForUpdate &&
            state.jackpot.lifeCycles.hasOwnProperty(keyForUpdate)
          ) {
            delete state.jackpot.lifeCycles[keyForUpdate];
          }
          break;
        }

        default:
          break;
      }
    },
    setJackpotNotification: (state, action) => {
      state.jackpot.wonNotifications.push(action.payload);
    },
    removeJackpotWonNotifications: (state) => {
      state.jackpot.wonNotifications = [];
    },
    setRecentlyWonJackpots: (state, action) => {
      state.jackpot.recentlyWonJackpots = action.payload.recentlyWon || [];
    },
    setIsHhOverlayOpen: (state, action) => {
      state.isHhOverlayOpen = !!action.payload;
    },
  },
});

export const {
  setHappyHour,
  setHappyHourBonus,
  updateHappyHourBonusState,
  removeHappyHourBonusState,
  setIsCongratsBanner,
  resetHappyHour,
  setHappyHourConfigs,
  setJackpotsLifecycles,
  updateJackpotsLifecycles,
  setJackpotNotification,
  removeJackpotWonNotifications,
  setRecentlyWonJackpots,
  setIsHhOverlayOpen,
} = promotionsSlice.actions;

export const selectIsCongratsBanner = (state) =>
  state.promotions.happyHourBonus.isCongratsBanner;

export const selectHhSchedules = createSelector(
  (state) => state.promotions.happyHourConfigs,
  (happyHourConfigs) => {
    if (!happyHourConfigs?.length) {
      return { nearestHh: null, hhSchedule: null };
    }

    const hhSchedule = happyHourConfigs.map((happyHour) => {
      const currentDate = new Date(Date.now() - happyHour.duration - 100);
      const schedule = getCronSchedule(happyHour.schedule, currentDate);
      return { ...happyHour, nextDate: schedule?.next() };
    });

    const nearestHh = hhSchedule.reduce((nearestPromotion, promotion) => {
      if (promotion.nextDate && nearestPromotion.nextDate) {
        return promotion.nextDate.getTime() <
          nearestPromotion.nextDate.getTime()
          ? promotion
          : nearestPromotion;
      }

      return nearestPromotion;
    }, hhSchedule[0]);

    return { nearestHh, hhSchedule };
  }
);

export const selectHHConfigs = (state) => state.promotions.happyHourConfigs;

export const selectIsJackpotsEnabled = (state) => {
  const lifecycles = state.promotions.jackpot.lifeCycles || {};
  return (
    state.promotions.jackpot.isJackpotsEnabled &&
    Object.keys(lifecycles).length > 0
  );
};

export const selectJackpotLifecycles = (state) =>
  state.promotions.jackpot.lifeCycles;

export const selectIsJackpotsInitialized = (state) =>
  state.promotions.jackpot.isInitialized;

export const selectHasJackpotLifecycles = (state) => {
  const lifecycles = state.promotions.jackpot.lifeCycles || {};
  return Object.keys(lifecycles).length > 0;
};

export const selectJackpotWonNotifications = (state) =>
  state.promotions.jackpot.wonNotifications;

export const selectIsShowJackpotModal = (state) =>
  state.promotions.jackpot.wonNotifications.length > 0;

export const selectJackpotWonBalance = (state) =>
  state.promotions.jackpot.wonNotifications.reduce(
    (sum, jackpot) => sum + jackpot.balance,
    0
  );

export const selectJackpotWonData = (state) => {
  const notifications = state.promotions.jackpot.wonNotifications;
  return notifications.length > 0 ? notifications[0] : null;
};

export const selectRecentlyWonJackpots = (state) =>
  state.promotions.jackpot.recentlyWonJackpots;

export const selectIsHhOverlayOpen = (state) => state.promotions.isHhOverlayOpen;



export default promotionsSlice.reducer;
